import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, getStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Sparkle, CheckCircle } from "@phosphor-icons/react";

const POLL_MS = 30000;

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      // Non-fatal background poll — log it so we can see persistent issues
      // in production, but don't pop a toast on every failed poll.
      console.warn("Failed to load notifications:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [user, load]);

  // Live push via the same /api/ws connection the rest of the app uses.
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    let reconnectTimer = null;
    const openSocket = () => {
      if (stopped) return;
      const token = getStoredToken();
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
      const wsUrl = token ? `${base}?token=${encodeURIComponent(token)}` : base;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "notification") {
            setItems((prev) => [payload.data, ...prev].slice(0, 50));
            setUnread((u) => u + 1);
          }
        } catch (parseErr) {
          console.warn("Bad notification frame:", parseErr);
        }
      };
      ws.onclose = () => {
        if (!stopped) reconnectTimer = setTimeout(openSocket, 3000);
      };
      ws.onerror = (errEvt) => {
        console.warn("Notification WS error", errEvt);
        try {
          ws.close();
        } catch (closeErr) {
          // Already closed/closing — nothing to do.
          console.warn("WS close after error:", closeErr);
        }
      };
    };
    openSocket();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        wsRef.current?.close();
      } catch (closeErr) {
        console.warn("WS unmount close:", closeErr);
      }
    };
  }, [user]);

  const handleClick = useCallback(async (n) => {
    try {
      if (!n.read) {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      }
      setOpen(false);
      if (n.link) navigate(n.link);
    } catch (err) {
      console.warn("Failed to mark notification read:", err);
      toast.error(formatApiError(err) || "Couldn't open notification");
    }
  }, [navigate]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (err) {
      console.warn("Failed to mark all read:", err);
      toast.error(formatApiError(err) || "Couldn't update notifications");
    }
  }, []);

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-cream transition-colors text-forest"
          aria-label="Notifications"
          data-testid="notification-bell"
        >
          <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-charcoal text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-count"
            >{unread > 99 ? "99+" : unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 bg-white border-sage" data-testid="notification-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-sage">
          <h4 className="font-display text-lg text-forest">Notifications</h4>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-forest hover:underline" data-testid="mark-all-read">Mark all read</button>
          )}
        </div>
        <ScrollArea className="max-h-[420px]">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sageGray text-sm">You're all caught up.</div>
          ) : (
            <ul className="divide-y divide-sage" data-testid="notification-list">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-cream transition-colors flex gap-3 ${n.read ? "" : "bg-gold/5"}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${n.read ? "bg-sage text-sageGray" : "bg-forest text-gold"}`}>
                      {n.type === "onboarding_completed" ? <Sparkle size={16} weight="fill" /> : <CheckCircle size={16} weight="fill" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${n.read ? "text-charcoal" : "text-forest font-medium"}`}>{n.title}</p>
                      <p className="text-xs text-sageGray mt-0.5">{n.body}</p>
                      {n.group_name && <p className="text-[10px] uppercase tracking-widest text-sageGray mt-1">{n.group_name}</p>}
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-gold shrink-0 mt-2" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
